import { BehaviorSubject } from 'rxjs';
import { WebrtcConfigAndUtils } from '../services/webrtc/webrtcConfigAndUtils';

/**
 * Observable that emits the current window size category ('small', 'medium', or 'large').
 * Automatically updates when the window is resized and crosses size thresholds.
 *
 * @example
 * ```typescript
 * import { windowSize } from 'rainbow-web-sdk';
 *
 * windowSize.subscribe(size => {
 *   console.log('Window size changed to:', size);
 * });
 * ```
 */
export const windowSize = new BehaviorSubject<string>(getWindowSize());

function getWindowSize(): string {
    if (typeof window === 'undefined') return 'large';
    const winWidth = window.innerWidth;
    if (winWidth < 720) return 'small';
    if (winWidth < 960) return 'medium';
    return 'large';
}

if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
        const newWindowSize = getWindowSize();
        if (newWindowSize !== windowSize.value) windowSize.next(newWindowSize);
    });
}

/**
 * Detects if the current browser is Internet Explorer.
 *
 * @returns `true` if the browser is Internet Explorer (any version), `false` otherwise
 *
 * @example
 * ```typescript
 * import { isIEBrowser } from 'rainbow-web-sdk';
 *
 * if (isIEBrowser()) {
 *   console.warn('IE is not fully supported');
 * }
 * ```
 */
export function isIEBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('msie') || ua.includes('trident/');
}

/**
 * Detects if the current browser is Safari.
 *
 * @returns `true` if the browser is Safari, `false` otherwise
 *
 * @remarks
 * Uses the WebRTC adapter's browser detection for accurate Safari identification.
 *
 * @example
 * ```typescript
 * import { isSafari } from 'rainbow-web-sdk';
 *
 * if (isSafari()) {
 *   console.log('Applying Safari-specific workarounds');
 * }
 * ```
 */
export function isSafari(): boolean {
    if (typeof window === 'undefined') return false;
    return window.adapter?.browserDetails?.browser === 'safari';
}

/**
 * Detects if the current browser is Firefox.
 *
 * @returns `true` if the browser is Firefox, `false` otherwise
 *
 * @remarks
 * Uses the WebRTC adapter's browser detection for accurate Firefox identification.
 *
 * @example
 * ```typescript
 * import { isFirefox } from 'rainbow-web-sdk';
 *
 * if (isFirefox()) {
 *   console.log('Running in Firefox');
 * }
 * ```
 */
export function isFirefox(): boolean {
    if (typeof window === 'undefined') return false;
    return window.adapter?.browserDetails?.browser === 'firefox';
}

/**
 * Detects if the application is running in a desktop environment (Electron or Qt WebEngine).
 *
 * @returns `true` if running in Electron or Qt WebEngine, `false` otherwise
 *
 * @example
 * ```typescript
 * import { isDesktop } from 'rainbow-web-sdk';
 *
 * if (isDesktop()) {
 *   console.log('Running as desktop application');
 * }
 * ```
 */
export function isDesktop(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('qtwebengine') || ua.includes('electron/');
}

/**
 * Detects if the application is running on localhost.
 *
 * @returns `true` if the hostname is 'localhost', `false` otherwise
 *
 * @example
 * ```typescript
 * import { isLocalhost } from 'rainbow-web-sdk';
 *
 * if (isLocalhost()) {
 *   console.log('Running in development mode');
 * }
 * ```
 */
export function isLocalhost(): boolean {
    if (typeof window === 'undefined') return false;
    return window.location.hostname === 'localhost';
}

/**
 * Detects the operating system platform from the user agent.
 *
 * @returns Platform identifier: 'mac', 'win', 'linux', or 'unknown'
 *
 * @example
 * ```typescript
 * import { platform } from 'rainbow-web-sdk';
 *
 * const os = platform();
 * console.log('Running on:', os);
 *
 * if (os === 'mac') {
 *   console.log('Use Cmd key for shortcuts');
 * }
 * ```
 */
export function platform(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('mac')) return 'mac';
    if (ua.includes('win')) return 'win';
    if (ua.includes('linux')) return 'linux';
    return 'unknown';
}

/**
 * Requests access to browser media devices (camera, microphone, screen sharing) with specified constraints.
 *
 * @param userMedia - Array of media constraints defining which devices to request
 * @returns Promise resolving to a MediaStream containing the requested media tracks
 *
 * @remarks
 * This function applies device constraints and settings before requesting media access.
 * Use {@link getBrowserMediaWithNoConstraints} if you need raw device access without constraints.
 *
 * @example
 * ```typescript
 * import { getBrowserMedia } from 'rainbow-web-sdk';
 *
 * // Request camera and microphone
 * const stream = await getBrowserMedia([
 *   { audio: true, video: { width: 1280, height: 720 } }
 * ]);
 * ```
 */
export const getBrowserMedia = async (userMedia: any[]): Promise<MediaStream> => {
    const webrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    return webrtcConfigAndUtils?.getBrowserMedia(userMedia);
};

/**
 * Requests access to browser media devices without applying additional constraints.
 *
 * @param userMedia - Array of basic media requests (audio/video flags)
 * @returns Promise resolving to a MediaStream with raw device access
 *
 * @remarks
 * Unlike {@link getBrowserMedia}, this function does not apply device-specific constraints
 * or settings, providing raw access to media devices.
 *
 * @example
 * ```typescript
 * import { getBrowserMediaWithNoConstraints } from 'rainbow-web-sdk';
 *
 * // Request basic camera and microphone access
 * const stream = await getBrowserMediaWithNoConstraints([
 *   { audio: true, video: true }
 * ]);
 * ```
 */
export const getBrowserMediaWithNoConstraints = async (userMedia: any[]): Promise<MediaStream> => {
    const webrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    return webrtcConfigAndUtils?.getBrowserMediaWithNoConstraints(userMedia);
};

/**
 * Copies text content to the system clipboard.
 *
 * @param data - The text string to copy to clipboard
 * @returns Promise that resolves when the text is copied
 *
 * @remarks
 * Automatically falls back to a legacy clipboard method if the Clipboard API is not available.
 * The fallback uses a temporary textarea element with document.execCommand('copy').
 *
 * @example
 * ```typescript
 * import { copyTextToClipboard } from 'rainbow-web-sdk';
 *
 * await copyTextToClipboard('Hello, World!');
 * console.log('Text copied to clipboard');
 * ```
 */
export const copyTextToClipboard = async (data: string) => {
    if (!navigator.clipboard) { fallbackCopyTextToClipboard(data); return; }
    await navigator.clipboard.writeText(data);
};

/**
 * Copies a ClipboardItem (which can contain multiple formats) to the system clipboard.
 *
 * @param data - ClipboardItem containing the data to copy (supports multiple MIME types)
 * @returns Promise that resolves when the data is copied
 *
 * @remarks
 * Supports rich clipboard content including images, HTML, and text. Falls back to text-only
 * copying if the Clipboard API is not available. The ClipboardItem can contain multiple
 * representations (e.g., both text and HTML) and the system will use the most appropriate one.
 *
 * @example
 * ```typescript
 * import { copyToClipboard } from 'rainbow-web-sdk';
 *
 * // Copy text and HTML to clipboard
 * const item = new ClipboardItem({
 *   'text/plain': new Blob(['Hello'], { type: 'text/plain' }),
 *   'text/html': new Blob(['<b>Hello</b>'], { type: 'text/html' })
 * });
 *
 * await copyToClipboard(item);
 * ```
 */
export const copyToClipboard = async (data: ClipboardItem) => {
    if (!navigator.clipboard) {
        const text = await data.getType('text/plain').then(blob => blob.text());
        fallbackCopyTextToClipboard(text);
        return;
    }
    await navigator.clipboard.write([data]);
};

const fallbackCopyTextToClipboard = (data: string): void => {
    const textArea = document.createElement('textarea');
    textArea.value = data;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try { document.execCommand('copy'); }
    finally { document.body.removeChild(textArea); }
};

export const isDarkTheme = (): boolean => {
    const className = $("html")[0].className;
    if (className && className.length > 0) return className.includes("theme--dark");
    return false;
};
